import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/lib/subjects";
import {
  GraduationCap, CalendarDays, Users, BookOpen, ClipboardList,
  LogOut, Menu, X, UsersRound, Sparkles, LayoutGrid, PencilLine, Trophy, BookText, Bell,
  LayoutDashboard, CalendarCheck2, Library, ClipboardCheck, FileText, FolderOpen, Database, ListTree, Mountain, ScanLine, Award,
} from "lucide-react";
import { EXAM_TRACK_GRADES } from "@/lib/subjects";

const NAV = {
  admin: [
    { to: "/admin/veri-girisi", label: "Öğrenci Veri Girişi", icon: Database, testid: "sidebar-nav-data-entry" },
    { to: "/admin/ogrenci-panelleri", label: "Öğrenci Sayfaları (Canlı Görünüm)", icon: UsersRound, testid: "sidebar-nav-student-pages" },
    { to: "/admin/kullanicilar", label: "Kullanıcı Yönetimi", icon: Users, testid: "sidebar-nav-users" },
    { to: "/admin/dersler", label: "Ders Tanımları", icon: BookOpen, testid: "sidebar-nav-subjects" },
    { to: "/admin/plan", label: "Haftalık Plan Düzenle", icon: ClipboardList, testid: "sidebar-nav-plan-editor" },
    { to: "/admin/calisma-programi", label: "Çalışma Programı Düzenle", icon: LayoutGrid, testid: "sidebar-nav-study-editor" },
    { to: "/admin/program-arsivi", label: "Program Arşivi", icon: FolderOpen, testid: "sidebar-nav-program-archive" },
    { to: "/ozet-ezber", label: "Özet & Ezber", icon: BookText, testid: "sidebar-nav-ozet" },
    { to: "/konu-takibi", label: "Konu Takibi", icon: ListTree, testid: "sidebar-nav-konu-takibi" },
    { to: "/matematik-kampi", label: "Matematik Kampı", icon: Mountain, testid: "sidebar-nav-matematik-kampi" },
    { to: "/optik-sinav", label: "Optik Sınav", icon: ScanLine, testid: "sidebar-nav-optik-sinav" },
    { to: "/karne-paneli", label: "Karne Paneli", icon: Award, testid: "sidebar-nav-karne-paneli" },
    { to: "/aylik-degerlendirme", label: "Aylık Değerlendirme", icon: CalendarCheck2, testid: "sidebar-nav-monthly" },
    { to: "/okuma-kitaplari", label: "Okuma Kitapları", icon: Library, testid: "sidebar-nav-books" },
    { to: "/admin/toplu-degerlendirme", label: "Sınıf Toplu Giriş", icon: Users, testid: "sidebar-nav-bulk-eval" },
    { to: "/gunluk-soru", label: "Günlük Soru Girişi", icon: PencilLine, testid: "sidebar-nav-daily-entry" },
    { to: "/sinav-takip", label: "Sınav Takip (4 & 7)", icon: ClipboardCheck, testid: "sidebar-nav-sinav-takip" },
    { to: "/deneme-karti", label: "Deneme Kartı", icon: FileText, testid: "sidebar-nav-deneme-karti" },
    { to: "/sinav-takvimi", label: "Sınav Takvimi", icon: CalendarDays, testid: "sidebar-nav-sinav-takvimi" },
    { to: "/kaynak-kutuphanesi", label: "Kaynak Kütüphanesi", icon: FolderOpen, testid: "sidebar-nav-kaynak" },
    { to: "/denemeler", label: "Zirve Sonuçları", icon: Trophy, testid: "sidebar-nav-exams" },
  ],
  koc: [
    { to: "/koc", label: "Öğrencilerim", icon: UsersRound, testid: "sidebar-nav-coach" },
    { to: "/sinav-takvimi", label: "Sınav Takvimi", icon: CalendarDays, testid: "sidebar-nav-sinav-takvimi" },
    { to: "/konu-takibi", label: "Konu Takibi", icon: ListTree, testid: "sidebar-nav-konu-takibi" },
    { to: "/matematik-kampi", label: "Matematik Kampı", icon: Mountain, testid: "sidebar-nav-matematik-kampi" },
    { to: "/optik-sinav", label: "Optik Sınav", icon: ScanLine, testid: "sidebar-nav-optik-sinav" },
    { to: "/karne-paneli", label: "Karne Paneli", icon: Award, testid: "sidebar-nav-karne-paneli" },
    { to: "/deneme-karti", label: "Deneme Kartı", icon: FileText, testid: "sidebar-nav-deneme-karti" },
    { to: "/kaynak-kutuphanesi", label: "Kaynak Kütüphanesi", icon: FolderOpen, testid: "sidebar-nav-kaynak" },
  ],
  ogrenci: [
    { to: "/genel-bakis", label: "Genel Bakış", icon: LayoutDashboard, testid: "sidebar-nav-overview" },
    { to: "/calisma-programi", label: "Çalışma Programı", icon: LayoutGrid, testid: "sidebar-nav-study" },
    { to: "/program", label: "Haftalık Planım", icon: CalendarDays, testid: "sidebar-nav-weekly-plan" },
    { to: "/aylik-degerlendirme", label: "Aylık Değerlendirme", icon: CalendarCheck2, testid: "sidebar-nav-monthly" },
    { to: "/okuma-kitaplari", label: "Okuma Kitapları", icon: Library, testid: "sidebar-nav-books" },
    { to: "/ozet-ezber", label: "Özet & Ezber", icon: BookText, testid: "sidebar-nav-ozet" },
    { to: "/konu-takibi", label: "Konu Takibi", icon: ListTree, testid: "sidebar-nav-konu-takibi" },
    { to: "/optik-sinav", label: "Optik Sınav", icon: ScanLine, testid: "sidebar-nav-optik-sinav" },
    { to: "/matematik-kampi", label: "Matematik Kampı", icon: Mountain, testid: "sidebar-nav-matematik-kampi" },
    { to: "/gunluk-soru", label: "Günlük Soru", icon: PencilLine, testid: "sidebar-nav-daily" },
    { to: "/sinav-takip", label: "Sınav Takip", icon: ClipboardCheck, testid: "sidebar-nav-sinav-takip", gradeOnly: EXAM_TRACK_GRADES },
    { to: "/deneme-karti", label: "Deneme Kartım", icon: FileText, testid: "sidebar-nav-deneme-karti" },
    { to: "/sinav-takvimi", label: "Sınav Takvimi", icon: CalendarDays, testid: "sidebar-nav-sinav-takvimi" },
    { to: "/kaynak-kutuphanesi", label: "Kaynaklarım", icon: FolderOpen, testid: "sidebar-nav-kaynak" },
    { to: "/denemeler", label: "Zirve Deneme Sonuçları", icon: Trophy, testid: "sidebar-nav-exams" },
  ],
  veli: [
    { to: "/genel-bakis", label: "Genel Bakış", icon: LayoutDashboard, testid: "sidebar-nav-overview" },
    { to: "/sinav-takvimi", label: "Sınav Takvimi", icon: CalendarDays, testid: "sidebar-nav-sinav-takvimi" },
    { to: "/calisma-programi", label: "Çalışma Programı", icon: LayoutGrid, testid: "sidebar-nav-study" },
    { to: "/program", label: "Haftalık Plan", icon: CalendarDays, testid: "sidebar-nav-weekly-plan" },
    { to: "/aylik-degerlendirme", label: "Aylık Değerlendirme", icon: CalendarCheck2, testid: "sidebar-nav-monthly" },
    { to: "/okuma-kitaplari", label: "Okuma Kitapları", icon: Library, testid: "sidebar-nav-books" },
    { to: "/ozet-ezber", label: "Özet & Ezber", icon: BookText, testid: "sidebar-nav-ozet" },
    { to: "/optik-sinav", label: "Optik Sınav", icon: ScanLine, testid: "sidebar-nav-optik-sinav" },
    { to: "/konu-takibi", label: "Konu Takibi", icon: ListTree, testid: "sidebar-nav-konu-takibi" },
    { to: "/matematik-kampi", label: "Matematik Kampı", icon: Mountain, testid: "sidebar-nav-matematik-kampi" },
    { to: "/gunluk-soru", label: "Günlük Soru", icon: PencilLine, testid: "sidebar-nav-daily" },
    { to: "/sinav-takip", label: "Sınav Takip", icon: ClipboardCheck, testid: "sidebar-nav-sinav-takip", gradeOnly: EXAM_TRACK_GRADES },
    { to: "/deneme-karti", label: "Deneme Kartı", icon: FileText, testid: "sidebar-nav-deneme-karti" },
    { to: "/kaynak-kutuphanesi", label: "Kaynaklarım", icon: FolderOpen, testid: "sidebar-nav-kaynak" },
    { to: "/denemeler", label: "Zirve Deneme Sonuçları", icon: Trophy, testid: "sidebar-nav-exams" },
  ],
};

function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/notifications").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const unread = items.filter((i) => !i.read).length;
  const toggle = async () => {
    const n = !open; setOpen(n);
    if (n && unread) { await api.post("/notifications/read").catch(() => {}); load(); }
  };
  return (
    <div className="relative no-print">
      <button data-testid="notif-bell" onClick={toggle} className="relative rounded-xl p-2 text-slate-600 transition hover:bg-slate-100">
        <Bell className="h-5 w-5" />
        {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 w-80 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
          <div className="px-3 py-2 text-sm font-bold text-slate-800">Bildirimler</div>
          {items.length === 0 ? <div className="px-3 py-6 text-center text-sm text-slate-400">Bildirim yok</div>
            : items.slice(0, 15).map((i) => (
              <div key={i.id} data-testid={`notif-${i.id}`} className={`rounded-xl px-3 py-2 text-sm ${i.read ? "text-slate-500" : "bg-indigo-50 font-medium text-slate-800"}`}>{i.message}</div>
            ))}
        </div>
      )}
    </div>
  );
}

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = (NAV[user?.role] || []).filter((it) => !it.gradeOnly || (user?.grade && it.gradeOnly.includes(user.grade)));

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Sidebar = (
    <aside className="flex h-full w-72 flex-col bg-[#1E293B] text-slate-200">
      <div className="flex items-center gap-3 bg-[#0F172A] px-5 py-5">
        <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-sky-500 flex items-center justify-center">
          <GraduationCap className="h-6 w-6 text-white" />
        </div>
        <div className="leading-tight">
          <div className="font-display text-lg font-extrabold text-white">ZOR</div>
          <div className="text-[10px] tracking-widest text-slate-400">NEW GENERATION ACADEMY</div>
        </div>
      </div>

      <div className="mx-4 mt-5 rounded-2xl bg-white/5 p-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center font-bold text-white">
            {initials(user?.name)}
          </div>
          <div className="min-w-0">
            <div className="truncate font-semibold text-white">{user?.name}</div>
            <div className="text-xs text-indigo-300">
              {ROLE_LABELS[user?.role] || user?.role}
              {user?.role === "ogrenci" && user?.grade ? ` · ${user.grade}. Sınıf` : ""}
            </div>
          </div>
        </div>
      </div>

      <nav className="mt-6 flex-1 space-y-1 px-3">
        <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Menü</p>
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all ${
                  isActive ? "bg-indigo-500 text-white shadow-lg shadow-indigo-900/40" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              {it.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-4">
        <div className="mb-3 flex items-center gap-2 rounded-xl bg-white/5 px-3 py-2 text-xs text-slate-400">
          <Sparkles className="h-4 w-4 text-amber-400" /> 2026 Eğitim Dönemi
          <span data-testid="app-version" className="ml-auto text-[10px] font-bold text-emerald-400">v09.10-KURUM</span>
        </div>
        <button
          data-testid="logout-btn"
          onClick={doLogout}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-rose-300 transition-all hover:bg-rose-500/10 active:scale-95"
        >
          <LogOut className="h-5 w-5" /> Çıkış Yap
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-[#FAF8F5]">
      <div className="hidden lg:block no-print">{Sidebar}</div>

      {/* mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden no-print">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex flex-1 flex-col min-w-0">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur sm:px-6 no-print">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-600" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <div className="font-display text-lg font-bold text-slate-800">ZOR NEW GENERATION ACADEMY</div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
              <div className="text-xs text-slate-500">{ROLE_LABELS[user?.role]}</div>
            </div>
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-sky-500 flex items-center justify-center text-sm font-bold text-white">
              {initials(user?.name)}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
